import { t } from "@lingui/core/macro";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useState } from "react";

import { cn } from "@/lib/utils";
import { AddPaymentMethodDialog } from "@/shared/components/payment-methods/AddPaymentMethodDialog";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Label } from "@/shared/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/shared/components/ui/radio-group";
import { type PayoutDestination, salesFromQuantities } from "@/util/withdrawal";
import { cashSaleEligibility, isPlaidVerified } from "@/util/withdrawalRules";

import {
  cashEstimateNote,
  HelpBlock,
  SaleLineRows,
  Screen,
  StepFooter,
  useFlow,
  useFlowNavigate,
} from "../../-components/Withdrawal/WithdrawalFlow";

const useResolvedBank = (): PayoutDestination | null => {
  const { inputs, cash } = useFlow();
  const match = inputs.paymentMethods.find((pm) => pm.id === cash.bankId && isPlaidVerified(pm));
  if (!match) return null;
  return {
    bankName: match.nickname,
    mask: match.mask,
    accountHolderName: match.accountHolderName,
  };
};

const PayoutScreen = () => {
  const navigate = useFlowNavigate();
  const { inputs, cash, setCash } = useFlow();
  const sales = salesFromQuantities(inputs.holdings, cash.quantities);
  const bank = useResolvedBank();
  const [addBankOpen, setAddBankOpen] = useState(false);
  if (!cashSaleEligibility({ holdings: inputs.holdings, quantities: cash.quantities, ...inputs.limits }).allowed)
    return <Navigate to="/trade/withdraw/cash" replace />;

  return (
    <Screen subtitle={t`Verify how you want to be paid.`} step={{ index: 3, total: 4 }}>
      <Card className="shadow-none">
        <CardHeader>
          <CardTitle className="text-base">{t`Verify your bank details`}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>{t`Deposit Account`}</Label>
            <p className="text-sm text-muted-foreground">
              {inputs.paymentMethods.some(isPlaidVerified)
                ? t`Only bank accounts verified through Plaid are shown.`
                : t`Connect a bank account through Plaid to receive your funds. If you do not want to use Plaid, contact your Relationship Manager.`}
            </p>
            <RadioGroup value={cash.bankId ?? ""} onValueChange={(value) => setCash({ bankId: String(value) })}>
              {inputs.paymentMethods.filter(isPlaidVerified).map((method) => (
                <label
                  key={method.id}
                  className={cn(
                    "flex items-start gap-3 rounded-md border p-3",
                    method.verified ? "cursor-pointer" : "cursor-not-allowed opacity-60",
                  )}
                >
                  <RadioGroupItem value={method.id} disabled={!method.verified} className="mt-0.5" />
                  <span className="flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium">{method.nickname}</span>
                    </span>
                    <span className="block text-xs text-muted-foreground capitalize">
                      {method.bankAccountType} ···· {method.mask}
                    </span>
                    {!method.verified && (
                      <span className="block text-xs text-muted-foreground">{t`Pending verification`}</span>
                    )}
                  </span>
                </label>
              ))}
            </RadioGroup>
            <Button variant="outline" size="sm" className="w-fit" onClick={() => setAddBankOpen(true)}>
              {t`Add a bank account`}
            </Button>
            <AddPaymentMethodDialog plaidOnly open={addBankOpen} onOpenChange={setAddBankOpen} />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">{t`Payout method`}</p>
            <RadioGroup
              value={cash.payoutMethod}
              onValueChange={(value) => {
                if (value === "ach" || value === "wire") setCash({ payoutMethod: value });
              }}
              className="grid gap-2 sm:grid-cols-2"
            >
              <label
                htmlFor="payout-wire"
                className={cn(
                  "flex cursor-pointer items-center gap-3 rounded-md border p-3",
                  cash.payoutMethod === "wire" && "border-primary bg-primary-highlight/30",
                )}
              >
                <RadioGroupItem id="payout-wire" value="wire" />
                <span className="text-sm font-medium">{t`Wire`}</span>
              </label>
              <label
                htmlFor="payout-ach"
                className={cn(
                  "flex cursor-pointer items-center gap-3 rounded-md border p-3",
                  cash.payoutMethod === "ach" && "border-primary bg-primary-highlight/30",
                )}
              >
                <RadioGroupItem id="payout-ach" value="ach" />
                <span className="text-sm font-medium">{t`ACH`}</span>
              </label>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardHeader>
          <CardTitle className="text-base">{t`Withdrawal summary`}</CardTitle>
        </CardHeader>
        <CardContent>
          <SaleLineRows sales={sales} totalLabel={t`Estimated Proceeds`} />
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground">{cashEstimateNote()}</p>

      <StepFooter onBack={() => navigate("cash")} onContinue={() => navigate("cash/review")} continueDisabled={!bank} />
      <HelpBlock />
    </Screen>
  );
};

export const Route = createFileRoute("/_authenticated/trade/withdraw/cash/payout")({
  component: PayoutScreen,
});
